import {
  useSetRouteContext
} from "@faire/web/common/routes/useRouteContext";
import { StrictExclude } from "@faire/web/common/typescriptUtils";
import {
  RouteRenderFn,
  UnattributedRoutePropsWithoutRenderFn,
  Route as WebRoute,
} from "@faire/web/ui/routing/Route";
import React from "react";
import { RouteComponentProps } from "react-router-dom";

import { FairePillar } from "./faire/logging/FairePillar";
import { FairePod } from "./faire/logging/FairePod";
import { ProductArea } from "./faire/logging/ProductArea";

export interface IRouteMetadata {
  productArea: keyof typeof ProductArea | undefined;
  pod: keyof typeof FairePod | undefined;
  pillar: keyof typeof FairePillar | undefined;
  templatedPath: string | undefined;
}

export type RoutePropsWithoutRenderFn = UnattributedRoutePropsWithoutRenderFn &
  IRouteMetadata;

export type RouteProps<
  T extends RoutePropsWithoutRenderFn,
  R extends RouteRenderFn,
> = T & {
  /** A more strongly-typed version of react-router-dom's Route render prop that prevents passing a React component as render prop which results in runtime errors */
  render?: StrictExclude<R, React.FC>;
};

/**
 * Route is a web-api specific thin wrapper around a react-router-dom Route, that
 * appends context of the product area associated with the web-api route (and
 * propagates it to DataDog DD_RUM).
 *
 * NOTE(luke): We can't simplify use of the Route tag API because the React Router Switch tag
 * makes use of the `path` prop on the direct descendant. This means we have to leverage the
 * Route tag in a shallow/repetitive way, which might look stupid, but it's necessary.
 * e.g. you can't do this:
 * ```
 * export const ReactRoute = () => <Route path={route} product_area={SOMETHING} .../>
 *
 * import { ReactRoute as MyRoute } from "@faire/web-api--source/routes/www/my-route";
 * <Switch>
 *   <MyRoute />
 * </Switch>
 * ```
 * The result is that `MyRoute` will always render - `Switch` treats it like a pathless
 * Route, since it doesn't (appear to) have a path prop. Instead, we simply have to have
 * to ensure `path` is a prop we specify at the node, e.g.
 * ```
 * import { props as myRouteProps } from "@faire/web-api--source/routes/www/my-route";
 * <Switch>
 *   <Route {...myRouteProps} />
 * </Switch>
 * ```
 */
export const Route = <
  T extends RoutePropsWithoutRenderFn,
  R extends RouteRenderFn,
>(
  props: RouteProps<T, R>
) => {
  const {
    path,
    productArea,
    pod,
    pillar,
    templatedPath,
    component: Component,
    render,
    ...otherProps
  } = props;

  useSetRouteContext(productArea, pod, pillar, templatedPath);

  return (
    <WebRoute
      path={path}
      render={(props: RouteComponentProps) => (
        <>
          {Component ? <Component {...props} /> : null}
          {render?.(props)}
        </>
      )}
      {...otherProps}
    />
  );
};
